import {Link} from 'react-router-dom';
import {ArrowRight, Check, Clock, LifeBuoy, Upload, type LucideIcon} from 'lucide-react';
import {useEnrollGroup} from '@client/pages/public/enroll/enrollContext';
import {renderRichText} from '@client/pages/public/enroll/richText';
import {
  ACCESS_CODE_TERM_TOKEN,
  buildHowToChecklist,
  MEDICAL_CHOICE_TOKEN,
  MEDICAL_KIND_TOKEN,
  MEDICAL_PLANS_TOKEN,
  medicalChoiceSentence,
  otherBenefitsTokens,
  PAY_PERIOD_TOKEN,
  resolveHowToSteps,
  siteHowToEnroll,
} from '@client/pages/public/enroll/groups/howToEnrollContent';
import {
  accessCodeEntryMode,
  groupOffersMedical,
  groupRequiresAccessCode,
  type ProductCategory,
} from '@client/pages/public/enroll/types';
import {POSTHOG_UNMASK_CLASS} from '@common-web/lib/analytics';
import {sitePayPeriod} from '@common/enrollmentSite/payPeriod';

const AND_FORMATTER = new Intl.ListFormat('en', {style: 'long', type: 'conjunction'});

// Categories shown on the "Choose your medical plan" step (regular medical + the AXIS limited-medical option).
const MEDICAL_STEP_CATEGORIES: ProductCategory[] = ['medical', 'limited-medical'];

export function EnrollHowToEnroll() {
  const group = useEnrollGroup();
  const base = `/${group.slug}`;
  const medicalPlanNames = group.plans
    .filter((plan) => MEDICAL_STEP_CATEGORIES.includes(plan.category))
    .map((plan) => plan.name);
  const medicalPlans = AND_FORMATTER.format(medicalPlanNames);
  // Everything offered beyond the medical step — drives step 3 so it only lists coverage the group sells.
  const otherLabels = group.products
    .filter((product) => !MEDICAL_STEP_CATEGORIES.includes(product.category))
    .map((product) => product.label.toLowerCase());
  const medicalKind = groupOffersMedical(group) ? 'medical' : 'limited medical';
  const otherTokens = [
    ...Object.entries(otherBenefitsTokens(otherLabels)),
    [MEDICAL_KIND_TOKEN, medicalKind],
    [MEDICAL_CHOICE_TOKEN, medicalChoiceSentence(medicalPlanNames)],
    [PAY_PERIOD_TOKEN, sitePayPeriod(group).adjective],
  ];
  const fillOther = (text: string) =>
    otherTokens.reduce((out, [token, value]) => out.replaceAll(token, value), text);
  const codeTerm = group.accessCodeTerm ?? 'access code';
  const gate = groupRequiresAccessCode(group) ? accessCodeEntryMode(group) : 'none';
  const content = siteHowToEnroll(group.howToEnroll, gate);
  const steps = resolveHowToSteps(content.steps, gate, otherLabels.length > 0);
  const checklist = buildHowToChecklist(group.checkout, gate).map((item) =>
    item.replace(ACCESS_CODE_TERM_TOKEN, codeTerm),
  );

  return (
    <div className={`mx-auto max-w-7xl space-y-10 ${POSTHOG_UNMASK_CLASS}`}>
      <header className='space-y-3'>
        <span className='inline-flex rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold tracking-wide text-blue-700'>
          How it works
        </span>
        <h1 className='text-4xl font-bold tracking-tight text-[var(--enroll-ink)]'>
          How to enroll in{' '}
          <span className='text-[var(--enroll-accent)]'>{steps.length} simple steps</span>
        </h1>
        <p className='max-w-2xl text-neutral-500'>{content.subtitle}</p>
      </header>

      <div className='grid gap-3 sm:grid-cols-3'>
        <InfoTile Icon={Clock} label='Time to enroll' value='~5 minutes' />
        <InfoTile Icon={Upload} label='Need to upload' value='Nothing' />
        <InfoTile Icon={LifeBuoy} label='Help available' value={group.supportPhone} />
      </div>

      <ol className='space-y-4'>
        {steps.map((step, index) => {
          const title = fillOther(step.title).replace(ACCESS_CODE_TERM_TOKEN, codeTerm);
          const body = fillOther(step.body)
            .replace(MEDICAL_PLANS_TOKEN, medicalPlans)
            .replace(ACCESS_CODE_TERM_TOKEN, codeTerm)
            .replaceAll('{contact}', group.benefitsContact ?? 'HR office');
          const tip = step.tip?.replace(ACCESS_CODE_TERM_TOKEN, codeTerm);
          return (
            <li
              key={step.title}
              className='flex gap-4 rounded-2xl border border-neutral-200 bg-white p-5'
            >
              <span className='inline-flex h-8 w-8 flex-none items-center justify-center rounded-full bg-[var(--enroll-accent)] text-sm font-bold text-white'>
                {index + 1}
              </span>
              <div className='space-y-3'>
                <h2 className='text-lg font-semibold text-[var(--enroll-ink)]'>{title}</h2>
                <p className='leading-relaxed text-neutral-600'>{renderRichText(body)}</p>
                {tip && (
                  <p className='rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm leading-relaxed text-amber-900'>
                    {renderRichText(tip)}
                  </p>
                )}
              </div>
            </li>
          );
        })}
      </ol>

      <section className='rounded-2xl border border-neutral-200 bg-white p-6'>
        <h2 className='text-xl font-semibold text-[var(--enroll-ink)]'>
          What you'll need before you start
        </h2>
        <ul className='mt-4 grid gap-x-8 gap-y-3 sm:grid-cols-2'>
          {checklist.map((item) => (
            <li key={item} className='flex items-start gap-2 text-sm text-neutral-600'>
              <Check className='mt-0.5 h-4 w-4 flex-none text-[var(--enroll-accent)]' />
              <span>{renderRichText(item)}</span>
            </li>
          ))}
        </ul>
      </section>

      <div className='flex justify-center'>
        <Link
          to={`${base}/medical`}
          className='inline-flex h-12 items-center gap-2 rounded-lg bg-[var(--enroll-accent)] px-7 text-base font-semibold text-white transition-colors hover:bg-[var(--enroll-accent-hover)]'
        >
          Enroll Now
          <ArrowRight className='h-[18px] w-[18px]' />
        </Link>
      </div>
    </div>
  );
}

function InfoTile({Icon, label, value}: {Icon: LucideIcon; label: string; value: string}) {
  return (
    <div className='flex items-center gap-3 rounded-xl border border-neutral-200 bg-white p-4'>
      <span className='inline-flex h-9 w-9 flex-none items-center justify-center rounded-full bg-blue-100 text-blue-700'>
        <Icon className='h-[18px] w-[18px]' />
      </span>
      <span className='leading-tight'>
        <span className='block text-xs uppercase tracking-wider text-neutral-400'>{label}</span>
        <span className='block text-sm font-semibold text-neutral-900'>{value}</span>
      </span>
    </div>
  );
}
